/**
 * BlazeProductCarousel — shared base class for product carousel sections
 * using BlazeSlider (product-recommendations, featured-collection, etc.).
 *
 * Subclasses should:
 * - Call `this.initSlider()` when ready to initialize
 * - Override `getSliderConfig()` if custom BlazeSlider config is needed
 *
 * Handles:
 * - BlazeSlider initialization with availability check
 * - Arrow navigation with disabled state at boundaries
 * - Reinitializes slider on breakpoint changes (BlazeSlider doesn't handle resize)
 * - Cleanup on disconnect
 *
 * Loaded per-section (may appear multiple times on a page).
 * Guard ensures the class is only defined once.
 */
if (!window.BlazeProductCarousel) {
class BlazeProductCarousel extends HTMLElement {
  constructor() {
    super();
    this.blazeSlider = null;
    this._mediaQueries = [];
    this._classObserver = null;
  }

  disconnectedCallback() {
    if (this.blazeSlider) {
      this.blazeSlider.destroy();
      this.blazeSlider = null;
    }
    if (this._classObserver) {
      this._classObserver.disconnect();
      this._classObserver = null;
    }
    this._mediaQueries.forEach(({ mql, handler }) => mql.removeEventListener('change', handler));
    this._mediaQueries = [];
  }

  /**
   * Override in subclasses to provide custom BlazeSlider config.
   * @returns {Object} BlazeSlider configuration object
   */
  getSliderConfig() {
    return {
      all: {
        enableAutoplay: false,
        enablePagination: false,
        slidesToShow: 4,
        slidesToScroll: 1,
        transitionDuration: 300,
        transitionTimingFunction: 'ease-in-out',
        loop: false,
        draggable: true,
        slideGap: '40px',
      },
      '(max-width: 749px)': {
        slidesToShow: 1.8,
        slideGap: '20px',
      },
      '(min-width: 750px) and (max-width: 989px)': {
        slidesToShow: 3,
        slideGap: '20px',
      },
    };
  }

  /**
   * Initialize the BlazeSlider. Call this from subclass when DOM is ready.
   */
  initSlider() {
    const sliderElement = this.querySelector('.blaze-slider');
    if (!sliderElement) return;

    const slides = sliderElement.querySelectorAll('.blaze-slide');
    if (slides.length <= 1) {
      this.classList.add('locked');
      return;
    }

    if (typeof window.BlazeSlider === 'undefined') {
      const script = document.querySelector('script[src*="blaze-slider"]');
      if (script) {
        script.addEventListener('load', () => this.initSlider(), { once: true });
      }
      return;
    }

    this.classList.remove('locked');

    try {
      this.blazeSlider = new window.BlazeSlider(sliderElement, this.getSliderConfig());

      this.setupNavigation();
      this.observeForDragSwipe(sliderElement);
      this.syncNavigationState();
      this.listenForBreakpointChanges();
    } catch (error) {
      console.error(`${this.tagName.toLowerCase()}: Error initializing BlazeSlider:`, error);
    }
  }

  /**
   * Check BlazeSlider's stateIndex to determine if we're at the first or last position.
   * stateIndex is updated synchronously by BlazeSlider after next()/prev().
   */
  isAtStart() {
    return this.blazeSlider && this.blazeSlider.stateIndex === 0;
  }

  isAtEnd() {
    if (!this.blazeSlider || !this.blazeSlider.states) return false;
    return this.blazeSlider.stateIndex >= this.blazeSlider.states.length - 1;
  }

  syncNavigationState() {
    this.updateNavigationState(this.isAtStart(), this.isAtEnd());
  }

  /**
   * Watch for class changes on the slider element to handle drag/swipe.
   * BlazeSlider updates stateIndex after drag settles, which triggers class changes.
   */
  observeForDragSwipe(sliderElement) {
    if (this._classObserver) {
      this._classObserver.disconnect();
    }

    this._classObserver = new MutationObserver(() => {
      this.syncNavigationState();
    });

    this._classObserver.observe(sliderElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  listenForBreakpointChanges() {
    var breakpoints = ['(max-width: 749px)', '(min-width: 750px) and (max-width: 989px)', '(min-width: 990px)'];
    breakpoints.forEach((query) => {
      var mql = window.matchMedia(query);
      var handler = (e) => {
        if (!e.matches) return;
        this.reinitSlider();
      };
      mql.addEventListener('change', handler);
      this._mediaQueries.push({ mql, handler });
    });
  }

  reinitSlider() {
    if (this._classObserver) {
      this._classObserver.disconnect();
    }
    if (this.blazeSlider) {
      this.blazeSlider.destroy();
      this.blazeSlider = null;
    }

    var sliderElement = this.querySelector('.blaze-slider');
    if (!sliderElement) return;

    this.blazeSlider = new window.BlazeSlider(sliderElement, this.getSliderConfig());

    this.observeForDragSwipe(sliderElement);
    this.syncNavigationState();
  }

  setupNavigation() {
    if (this._navigationReady) return;
    this._navigationReady = true;
    this.querySelectorAll('.js-slide-arrow').forEach((button) => {
      button.addEventListener('click', (event) => this.onArrowClicked(event));
    });
  }

  onArrowClicked(event) {
    if (!this.blazeSlider) return;

    var direction = event.currentTarget.getAttribute('data-direction');
    if (direction === 'prev') {
      this.blazeSlider.prev();
    } else {
      this.blazeSlider.next();
    }

    this.syncNavigationState();
  }

  updateNavigationState(atStart, atEnd) {
    this.querySelectorAll('.js-slide-arrow').forEach((button) => {
      var direction = button.getAttribute('data-direction');

      if (direction === 'prev' && atStart) {
        button.setAttribute('disabled', 'true');
      } else if (direction === 'next' && atEnd) {
        button.setAttribute('disabled', 'true');
      } else {
        button.removeAttribute('disabled');
      }
    });
  }
}

window.BlazeProductCarousel = BlazeProductCarousel;
}
